import { TimeZoneDetails } from '@tableau/self-service-schedules';
import { SiteUrls } from '@tableau/shared-urls';

import { ServerUrls } from '../../app/ServerUrls';
import { ScheduleSubspaceName } from '../../app/state/SpaceInfo';
import { Messages } from '../../codegen/Localize';
import { getSessionManagementServiceInstance } from '../../sessionManagement/SessionManagementService';
import { ExecutionType, ScheduleAction, ScheduleActions } from '../types/ISchedule';

export function siteScheduleUrl(
  siteUrls: SiteUrls,
  scheduleId: string,
  scheduleAction: string, // ScheduleAction = 'Extract' | 'Flow' | 'Subscription'
): string {
  switch (scheduleAction) {
    case ScheduleActions.Extract:
      return siteUrls.scheduleExtractRefreshesUrl(scheduleId);
    case ScheduleActions.Flow:
      return siteUrls.scheduleFlowsUrl(scheduleId);
    case ScheduleActions.Subscription:
      return siteUrls.scheduleSubscriptionsUrl(scheduleId);
    case ScheduleActions.Data_Acceleration:
      return siteUrls.scheduleDetailsUrl(scheduleId);
  }
  return siteUrls.scheduleUrl(scheduleId);
}

export function serverScheduleUrl(
  serverUrls: ServerUrls,
  scheduleId: string,
  scheduleAction: string, // ScheduleAction = 'Extract' | 'Flow' | 'Subscription'
): string {
  switch (scheduleAction) {
    case ScheduleActions.Extract:
      return serverUrls.scheduleExtractRefreshesUrl(scheduleId);
    case ScheduleActions.Flow:
      return serverUrls.scheduleFlowsUrl(scheduleId);
    case ScheduleActions.Subscription:
      return serverUrls.scheduleSubscriptionsUrl(scheduleId);
    case ScheduleActions.Data_Acceleration:
      return serverUrls.scheduleDetailsUrl(scheduleId);
  }
  return serverUrls.scheduleUrl(scheduleId);
}

export function schedulePath(
  urls: ServerUrls | SiteUrls,
  scheduleId: string,
  scheduleAction: string, // ScheduleAction = 'Extract' | 'Flow' | 'Subscription'
): string {
  switch (scheduleAction) {
    case ScheduleActions.Extract:
      return urls.scheduleExtractRefreshesPath(scheduleId);
    case ScheduleActions.Flow:
      return urls.scheduleFlowsPath(scheduleId);
    case ScheduleActions.Subscription:
      return urls.scheduleSubscriptionsPath(scheduleId);
  }
  return urls.schedulePath(scheduleId);
}

export function scheduleSubspaceNameMatchesSchedule(subspaceName: ScheduleSubspaceName, scheduledAction: string): boolean {
  return (subspaceName === 'extractRefreshes' && scheduledAction === ScheduleActions.Extract) ||
    (subspaceName === 'subscriptions' && scheduledAction === ScheduleActions.Subscription) ||
    (subspaceName === 'flows' && scheduledAction === ScheduleActions.Flow);
}

export function localizeExecutionType(executionType: ExecutionType): string {
  switch (executionType) {
    case ExecutionType.Parallel:
      return Messages.adminSelfServiceSchedulesDialogExecutionParallel();
    case ExecutionType.Serial:
      return Messages.adminSelfServiceSchedulesDialogExecutionSerial();
    default:
      throw `Invalid execution type: ${executionType}`;
  }
}

export function localizeScheduleAction(scheduledAction: ScheduleAction): string {
  switch (scheduledAction) {
    case ScheduleAction.Data_Acceleration:
      return Messages.adminSelfServiceSchedulesDialogTypeAcceleration();
    case ScheduleAction.Extract:
      return Messages.adminSelfServiceSchedulesDialogTypeExtractRefresh();
    case ScheduleAction.Flow:
      return Messages.adminSelfServiceSchedulesDialogTypeFlow();
    case ScheduleAction.Subscription:
      return Messages.adminSelfServiceSchedulesDialogTypeSubscription();
    default:
      throw `Invalid scheduled action: ${scheduledAction}`;
  }
}

export function getPriorityRange(scheduledAction: ScheduleAction): { min: number, max: number } {
  switch (scheduledAction) {
    case ScheduleActions.Data_Acceleration:
      return { min: 75, max: 100 };
    default:
      return { min: 1, max: 100 };
  }
}

export function getDefaultTimeZoneDetails(): TimeZoneDetails {
  const server = getSessionManagementServiceInstance();
  const userInfo = server.sessionUserInfo();
  const userAccountSettingsLink = new SiteUrls({
    siteUrlName: server.sessionSiteUrlName(),
    queryString: window.location.search
  }).userSettingsUrl(userInfo.username, userInfo.domainName);
  return {
    timeZonesEnabled: false,
    timeZoneDisplayName: server.userTimeZone().displayName,
    linkToUserSettings: userAccountSettingsLink,
  };
}
